import React, { useEffect, useRef, useState } from "react";
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import { AccountModalBody, AccountModalFooter, AccountModalHeader } from "./AccountModalParts";
import ResetPassword from "./ResetPassword";
import { changeUserPassword } from "./Account.api";
import { forgotUser } from "../ForgotForm/ForgotForm.api";
import { toastrError, toastrSuccess } from "lib/toastr";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "context/auth-context";
import { validatePassword } from "./PasswordValidation";
import { verifyCurrentPassword } from "components/LoginForm/LoginForm.api";

const eyeButtonClass =
  "absolute inset-y-0 right-0 inline-flex items-center justify-center w-10 text-ink-4 hover:text-ink-2";

interface AccountToggleProps {
  isPopupOpen1: () => void;
}

const ChangePassword: React.FC<AccountToggleProps> = ({ isPopupOpen1 }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { auth } = useAuth();
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [currentPasswordMatches, setCurrentPasswordMatches] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentError, setCurrentError] = useState("");
  const [newError, setNewError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [issubmit, setissubmit] = useState(false);
  const modelRef = useRef();
  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const mutation = useMutation(() => changeUserPassword(auth?.panelistId, password, newPassword), {
    onSuccess: (response: any) => {
      if (response.success) {
        toastrSuccess(response.message);
      } else {
        toastrError(t("changePassword.terminate"));
      }
    },
    onError: () => {
      toastrError(t("changePassword.terminate"));
    },
  });

  // The current password is checked on the server and only a boolean comes
  // back. The browser no longer receives any password hash.
  const verifyCurrent = useMutation((candidate: string) => verifyCurrentPassword(candidate), {
    onSuccess: (response: any) => {
      setCurrentPasswordMatches(!!response?.result?.matches);
    },
    onError: () => {
      // Fail closed: an unverifiable current password is not a match.
      setCurrentPasswordMatches(false);
    },
  });

  useEffect(() => {
    if (!password) {
      setCurrentPasswordMatches(false);
      return;
    }
    verifyCurrent.mutate(password);
  }, [password]);

  const handleReset = (e) => {
    e.preventDefault();
    setissubmit(true);
    if (
      !validatePassword(
        password,
        newPassword,
        confirmPassword,
        currentPasswordMatches,
        setConfirmError,
        setNewError,
        setCurrentError,
        t
      )
    ) {
      return;
    }

    mutation.mutate();
    setNewError("");
    setConfirmError("");
    setCurrentError("");
    isPopupOpen1();
  };

  const ResetPasswordMutaion = useMutation(() => forgotUser(auth.email), {
    onSuccess: (response: any) => {
      if (response.success) {
        toastrSuccess(t("forgotPassword.success"));
      } else {
        toastrError(response.message);
      }
    },
    onError: () => {
      toastrError(t("page.unsubscribe.Encountered"));
    },
  });

  const togglepopup = () => {
    setIsPopupOpen(!isPopupOpen);
    ResetPasswordMutaion.mutate();
  };

  const CloseModel = (e) => {
    if (modelRef.current == e.target) {
      isPopupOpen1();
    }
  };

  useEffect(() => {
    if (issubmit) {
      validatePassword(
        password,
        newPassword,
        confirmPassword,
        currentPasswordMatches,
        setConfirmError,
        setNewError,
        setCurrentError,
        t
      );
    }
  }, [t]);

  return (
    <>
      {isPopupOpen1 && (
        <div
          className="zs-backdrop"
          ref={modelRef}
          onClick={CloseModel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-password-title"
        >
          <div className="zs-modal max-w-md">
            <AccountModalHeader
              titleId="account-password-title"
              title={t("changePassword.head")}
              icon={<FiLock />}
              onClose={isPopupOpen1}
            />
            <AccountModalBody>
              {/* Current Password Field */}
              <div>
                <label className="zs-label">{t("changePassword.current_pass")}</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    className={`zs-input pr-11 ${currentError ? "is-invalid" : ""}`}
                    onChange={(e) => {setPassword(e.target.value); setCurrentError("");}}
                  />
                  <button
                    type="button"
                    className={eyeButtonClass}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                  </button>
                </div>
                {currentError && <p className="zs-error">{currentError}</p>}
              </div>

              {/* New Password Field */}
              <div>
                <label className="zs-label">{t("changePassword.setnew_pass")}</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className={`zs-input pr-11 ${confirmError ? "is-invalid" : ""}`}
                    onChange={(e) => {setNewPassword(e.target.value); setConfirmError("");}}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                  />
                  <button
                    type="button"
                    className={eyeButtonClass}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                  </button>
                </div>
                {confirmError ? (
                  <p className="zs-error">{confirmError}</p>
                ) : (
                  <p className="zs-hint">6 to 16 characters, no spaces.</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="zs-label">{t("changePassword.confnew_pass")}</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`zs-input pr-11 ${newError ? "is-invalid" : ""}`}
                    onChange={(e) => {setConfirmPassword(e.target.value); setNewError("");}}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                  />
                  <button
                    type="button"
                    className={eyeButtonClass}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
                  </button>
                </div>
                {newError && <p className="zs-error">{newError}</p>}
              </div>
            </AccountModalBody>
            <AccountModalFooter className="sm:!justify-between">
              <p className="text-xs text-center sm:text-left text-ink-3">
                {t("changePassword.notRemember")}
                <span className="ml-1 cursor-pointer zs-link" onClick={togglepopup}>
                  {t("changePassword.forgotbutton")}.
                </span>
              </p>
              <button className="zs-btn zs-btn-primary" onClick={handleReset}>
                {t("changePassword.button")}
              </button>
            </AccountModalFooter>
          </div>
          {isPopupOpen && (
            <div>
              <ResetPassword isPopupOpen={() => setIsPopupOpen(false)} isPopupOpen1={isPopupOpen1} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChangePassword;
